/**
 * Channel mutation operations
 * @see src/services/channels/index.ts for core operations
 * @see src/services/channels/queries.ts for query operations
 */

import { supabase } from '@/lib/supabase';
import { changeTrackingService } from '../changeTracking';
import { getTrackableFields } from '../changeTracking/fieldDefinitions';
// Channel type imported as needed

interface CreateChannelInput {
  providerId: number;
  versionId: number;
  name: string;
  number: number;
  price?: number | null;
  description?: string | null;
  categoryId?: number | null;
  categoryIds?: number[];
  defaultCategoryId?: number | null;
  planIds: number[];
  is_local?: boolean;
  is_duplicate?: boolean;
  linked_provider_id?: number | null;
}

interface UpdateChannelInput {
  providerId: number;
  versionId: number;
  id: number;
  name: string;
  number: number;
  price?: number | null;
  description?: string | null;
  categoryId?: number | null;
  planIds: number[];
  categoryIds?: number[];
  defaultCategoryId?: number | null;
  is_local?: boolean;
  is_duplicate?: boolean;
  linked_provider_id?: number | null;
}

interface ChannelResponse {
  data: any; // Raw database response, will be cast to Channel type where needed
  error: any;
}

interface DeleteChannelsParams {
  ids: number[];
  providerId: number;
  versionId: number;
}

// Add interface for bulk update with multiple categories
interface BulkUpdateChannelsInput {
  channelIds: number[];
  providerId: number;
  versionId: number;
  category_id?: number | null;
  category_ids?: number[];
  default_category_id?: number | null;
  categoryUpdateMode?: 'add' | 'replace' | 'remove';
  plan_ids?: number[];
  description?: string;
  price?: number | null;
  is_local?: boolean;
  is_duplicate?: boolean;
}

export const channelMutations = {
  createChannel: async (data: CreateChannelInput): Promise<ChannelResponse> => {
    console.log('[Mutations] Create Channel Input:', {
      ...data,
      price: data.price,
      description: data.description,
      categoryIds: data.categoryIds,
      defaultCategoryId: data.defaultCategoryId
    });

    // Use centralized validation service
    const { channelValidationService } = await import('./validation');
    
    const validationContext = {
      providerId: data.providerId,
      versionId: data.versionId,
      is_local: data.is_local,
      is_duplicate: data.is_duplicate
    };

    const channelData = {
      name: data.name,
      numbers: data.number ? [{ typeId: 1, number: data.number.toString() }] : []
    };

    const validation = await channelValidationService.validateChannel(channelData, validationContext);
    
    if (!validation.isValid) {
      console.error('[Mutations] Channel validation failed:', validation.errors);
      throw new Error(validation.errors[0] || 'Channel validation failed');
    }

    const insertData = {
      name: data.name,
      number: data.number,
      description: data.description,
      price: data.price,
      provider_id: data.providerId,
      version_id: data.versionId,
      category_id: data.categoryId, // Keep for backward compatibility
      is_local: data.is_local,
      is_duplicate: data.is_duplicate,
      linked_provider_id: data.linked_provider_id
    };

    console.log('[Mutations] Supabase Insert Data:', insertData);

    // First create the channel
    const { data: channel, error } = await supabase
      .from('channels')
      .insert(insertData as never)
      .select()
      .single();

    console.log('[Mutations] Supabase Response:', { 
      data: channel, 
      error,
      insertedPrice: channel?.price,
      insertedDescription: channel?.description
    });

    if (error) throw error;

    // Handle multiple categories if provided
    if (data.categoryIds && data.categoryIds.length > 0) {
      // Determine the default category ID
      const defaultCategoryId = data.defaultCategoryId || data.categoryIds[0];
      
      // Insert category relationships
      const { error: insertCategoryError } = await supabase
        .from('channel_categories')
        .insert(
          data.categoryIds.map(categoryId => ({
            channel_id: channel.id,
            category_id: categoryId,
            is_default: !!(categoryId === defaultCategoryId)
          }))
        );

      if (insertCategoryError) throw insertCategoryError;
    }

    // Then create plan associations if any plans are selected
    if (data.planIds.length > 0) {
      const { error: planError } = await supabase
        .from('channel_plans')
        .insert(
          data.planIds.map(planId => ({
            channel_id: channel.id,
            plan_id: planId,
            version_id: data.versionId
          }))
        );

      if (planError) throw planError;

      // Track plan assignments with JSON format for new channel
      try {
        const { data: planDetails } = await supabase
          .from('plans')
          .select('id, name, color_index')
          .in('id', data.planIds);

        const { data: version } = await supabase
          .from('provider_versions')
          .select('region_id')
          .eq('id', data.versionId)
          .single();

        if (planDetails && version) {
          for (const planData of planDetails) {
            await changeTrackingService.logPlanAssignment({
              versionId: data.versionId,
              providerId: data.providerId,
              regionId: version.region_id,
              channelId: channel.id,
              actionType: 'CREATE',
              newPlan: {
                id: planData.id,
                name: planData.name,
                color: planData.color_index || 1
              },
            });
          }
        }
      } catch (trackingError) {
        console.error('[Mutations] Plan tracking failed for new channel:', trackingError);
      }
    }

    // Track channel creation
    try {
      // Get version data for region_id
      const { data: version } = await supabase
        .from('provider_versions')
        .select('region_id')
        .eq('id', data.versionId)
        .single();

      if (version) {
        await changeTrackingService.logEntityCreation({
          versionId: data.versionId,
          providerId: data.providerId,
          regionId: version.region_id,
          entityType: 'channels',
          entityId: channel.id,
          entityData: {
            id: channel.id,
            name: channel.name,
            description: channel.description,
            price: channel.price,
            category_id: channel.category_id,
            is_local: channel.is_local,
            is_duplicate: channel.is_duplicate
          }
        });
      }
    } catch (trackingError) {
      console.error('[Mutations] Failed to track channel creation:', trackingError);
    }

    return { data: channel, error: null };
  },

  updateChannel: async (data: UpdateChannelInput): Promise<ChannelResponse> => {
    console.log('[Mutations] Update Channel Input:', {
      ...data,
      price: data.price,
      description: data.description,
      categoryIds: data.categoryIds,
      defaultCategoryId: data.defaultCategoryId
    });

    // Get current channel data and region info for change tracking
    const { data: currentChannel, error: fetchError } = await supabase
      .from('channels')
      .select('*')
      .eq('id', data.id)
      .single();

    if (fetchError) {
      console.error('[Mutations] Error fetching current channel:', fetchError);
      throw fetchError;
    }

    // Get region_id from version
    const { data: version, error: versionError } = await supabase
      .from('provider_versions')
      .select('region_id')
      .eq('id', data.versionId)
      .single();

    if (versionError) {
      console.error('[Mutations] Error fetching version region:', versionError);
      throw versionError;
    }

    // Use centralized validation service for updates
    if (data.name) {
      const { channelValidationService } = await import('./validation');
      
      const validationContext = {
        providerId: data.providerId,
        versionId: data.versionId,
        excludeChannelId: data.id, // Exclude current channel from duplicate check
        is_local: data.is_local,
        is_duplicate: data.is_duplicate
      };

      const channelData = {
        name: data.name,
        numbers: data.number ? [{ typeId: 1, number: data.number.toString() }] : []
      };

      const validation = await channelValidationService.validateChannel(channelData, validationContext);
      
      if (!validation.isValid) {
        console.error('[Mutations] Channel update validation failed:', validation.errors);
        throw new Error(validation.errors[0] || 'Channel validation failed');
      }
    }

    // Update channel details in the channels table
    const { data: channel, error } = await supabase
      .from('channels')
      .update({
        name: data.name,
        number: data.number,
        description: data.description,
        price: data.price,
        category_id: data.categoryId, // Keep for backward compatibility
        is_local: data.is_local,
        is_duplicate: data.is_duplicate,
        linked_provider_id: data.linked_provider_id
      } as never)
      .eq('id', data.id)
      .select()
      .single();

    console.log('[Mutations] Update Channel Response:', { data: channel, error });

    if (error) throw error;

    // Track changes for channel fields
    try {
      const newChannelData = {
        name: data.name,
        description: data.description,
        price: data.price,
        category_id: data.categoryId,
        is_local: data.is_local,
        is_duplicate: data.is_duplicate,
      };

      const changes = changeTrackingService.generateFieldChanges(
        currentChannel,
        newChannelData,
        [...getTrackableFields('channels')]
      );

      if (changes.length > 0) {
        await changeTrackingService.logBulkChanges({
          versionId: data.versionId,
          providerId: data.providerId,
          regionId: version.region_id,
          entityType: 'channels',
          entityId: data.id,
          changes,
        });
      }
    } catch (changeTrackingError) {
      console.error('[Mutations] Change tracking failed:', changeTrackingError);
      // Don't throw - change tracking failure shouldn't break the main operation
    }

    // Handle multiple categories if provided
    if (data.categoryIds && data.categoryIds.length > 0) {
      console.log('[Mutations] Updating categories for channel:', {
        channelId: data.id,
        categoryIds: data.categoryIds,
        defaultCategoryId: data.defaultCategoryId
      });
      
      // First delete existing category relationships
      const { error: deleteCategoryError } = await supabase
        .from('channel_categories')
        .delete()
        .eq('channel_id', data.id);

      if (deleteCategoryError) {
        console.error('[Mutations] Error deleting categories:', deleteCategoryError);
        throw deleteCategoryError;
      }

      // Determine the default category ID
      const defaultCategoryId = data.defaultCategoryId || data.categoryIds[0];

      // Then insert new category relationships
      const categoryRecords = data.categoryIds.map(categoryId => ({
        channel_id: data.id,
        category_id: categoryId,
        is_default: !!(categoryId === defaultCategoryId)
      }));
      
      console.log('[Mutations] Inserting new category records:', categoryRecords);
      
      const { error: insertCategoryError } = await supabase
        .from('channel_categories')
        .insert(categoryRecords);

      if (insertCategoryError) {
        console.error('[Mutations] Error inserting categories:', insertCategoryError);
        throw insertCategoryError;
      }
    }

    // Update plan associations
    // First delete existing associations
    const { error: deleteError } = await supabase
      .from('channel_plans')
      .delete()
      .eq('channel_id', data.id);

    if (deleteError) throw deleteError;

    // Then create new associations if any plans are selected
    if (data.planIds.length > 0) {
      const { error: planError } = await supabase
        .from('channel_plans')
        .insert(
          data.planIds.map(planId => ({
            channel_id: data.id,
            plan_id: planId,
            version_id: data.versionId
          }))
        );

      if (planError) throw planError;
    }

    // Track relationship changes with enhanced JSON format
    try {
      // Get current plan and category data for comparison
      const { data: currentPlans } = await supabase
        .from('channel_plans')
        .select(`
          plan_id,
          plans:plan_id (name, color_index)
        `)
        .eq('channel_id', data.id);

      const { data: currentCategories } = await supabase
        .from('channel_categories')
        .select(`
          category_id,
          categories:category_id (name)
        `)
        .eq('channel_id', data.id);

      const currentPlanIds = (currentPlans || []).map(cp => cp.plan_id);
      const currentCategoryIds = (currentCategories || []).map(cc => cc.category_id);

      // Track category changes if provided
      if (data.categoryIds && data.categoryIds.length > 0) {
        // Get category details for JSON tracking
        const { data: categoryDetails } = await supabase
          .from('categories')
          .select('id, name')
          .in('id', data.categoryIds);

        const categoryMap = new Map(categoryDetails?.map(c => [c.id, c]) || []);

        // Log each new category assignment
        for (const categoryId of data.categoryIds) {
          if (!currentCategoryIds.includes(categoryId)) {
            const categoryData = categoryMap.get(categoryId);
            if (categoryData) {
              await changeTrackingService.logCategoryAssignment({
                versionId: data.versionId,
                providerId: data.providerId,
                regionId: version.region_id,
                channelId: data.id,
                actionType: 'CREATE',
                newCategory: { id: categoryData.id, name: categoryData.name },
              });
            }
          }
        }
      }

      // Track plan assignment changes with JSON format
      if (data.planIds.length > 0) {
        // Get plan details for JSON tracking
        const { data: planDetails } = await supabase
          .from('plans')
          .select('id, name, color_index')
          .in('id', data.planIds);

        const planMap = new Map(planDetails?.map(p => [p.id, p]) || []);

        // Log each new plan assignment
        for (const planId of data.planIds) {
          if (!currentPlanIds.includes(planId)) {
            const planData = planMap.get(planId);
            if (planData) {
              await changeTrackingService.logPlanAssignment({
                versionId: data.versionId,
                providerId: data.providerId,
                regionId: version.region_id,
                channelId: data.id,
                actionType: 'CREATE',
                newPlan: {
                  id: planData.id,
                  name: planData.name,
                  color: planData.color_index || 1
                },
              });
            }
          }
        }
      }

      // Track removed plans
      for (const currentPlanId of currentPlanIds) {
        if (!data.planIds.includes(currentPlanId)) {
          const currentPlan = (currentPlans || []).find(cp => cp.plan_id === currentPlanId);
          if (currentPlan?.plans) {
            await changeTrackingService.logPlanAssignment({
              versionId: data.versionId,
              providerId: data.providerId,
              regionId: version.region_id,
              channelId: data.id,
              actionType: 'DELETE',
              oldPlan: {
                id: currentPlanId,
                name: (currentPlan.plans as any)?.name || `Plan #${currentPlanId}`,
                color: (currentPlan.plans as any)?.color_index || 1
              },
            });
          }
        }
      }
    } catch (relationshipTrackingError) {
      console.error('[Mutations] Enhanced relationship change tracking failed:', relationshipTrackingError);
      // Don't throw - change tracking failure shouldn't break the main operation
    }

    return { data: channel, error: null };
  },

  deleteChannel: async (channelId: number) => {
    // Get channel data before deletion for change tracking
    const { data: channelToDelete, error: fetchError } = await supabase
      .from('channels')
      .select(`
        *,
        provider_versions!version_id(region_id)
      `)
      .eq('id', channelId)
      .single();

    if (fetchError) {
      console.error('Error fetching channel for deletion tracking:', fetchError);
      throw fetchError;
    }

    const { error } = await supabase
      .from('channels')
      .delete()
      .eq('id', channelId);

    if (error) throw error;

    // Track channel deletion
    if (channelToDelete) {
      try {
        await changeTrackingService.logEntityDeletion({
          versionId: channelToDelete.version_id!,
          providerId: channelToDelete.provider_id!,
          regionId: (channelToDelete.provider_versions as any)[0]?.region_id,
          entityType: 'channels',
          entityId: channelId,
          entityData: {
            id: channelToDelete.id,
            name: channelToDelete.name,
            price: channelToDelete.price,
            description: channelToDelete.description,
            is_local: channelToDelete.is_local,
            is_duplicate: channelToDelete.is_duplicate
          }
        });
      } catch (trackingError) {
        console.error('Failed to track channel deletion:', trackingError);
      }
    }

    return { error: null };
  },

  bulkUpdateCategory: async (channelIds: number[], categoryId: number | null) => {
    const { error } = await supabase
      .from('channels')
      .update({ category_id: categoryId })
      .in('id', channelIds);

    if (error) throw error;
  },

  bulkUpdatePlans: async (channelIds: number[], planIds: number[], versionId: number) => {
    // First, remove existing plan associations
    const { error: deleteError } = await supabase
      .from('channel_plans')
      .delete()
      .in('channel_id', channelIds);

    if (deleteError) throw deleteError;

    if (planIds.length > 0) {
      // Then, create new plan associations
      const { error: insertError } = await supabase
        .from('channel_plans')
        .insert(
          channelIds.flatMap(channelId =>
            planIds.map(planId => ({
              channel_id: channelId,
              plan_id: planId,
              version_id: versionId
            }))
          )
        );

      if (insertError) throw insertError;
    }
  },

  deleteChannels: async (params: DeleteChannelsParams) => {
    // Get channel data before deletion for change tracking
    const { data: channelsToDelete, error: fetchError } = await supabase
      .from('channels')
      .select(`
        *,
        provider_versions!version_id(region_id)
      `)
      .in('id', params.ids);

    if (fetchError) {
      console.error('Error fetching channels for deletion tracking:', fetchError);
      throw fetchError;
    }

    const { error } = await supabase
      .from('channels')
      .delete()
      .in('id', params.ids);

    if (error) throw error;

    // Track channel deletions
    if (channelsToDelete && channelsToDelete.length > 0) {
      try {
        for (const channelToDelete of channelsToDelete) {
          await changeTrackingService.logEntityDeletion({
            versionId: channelToDelete.version_id!,
            providerId: channelToDelete.provider_id!,
            regionId: (channelToDelete.provider_versions as any)[0]?.region_id,
            entityType: 'channels',
            entityId: channelToDelete.id,
            entityData: {
              id: channelToDelete.id,
              name: channelToDelete.name,
              price: channelToDelete.price,
              description: channelToDelete.description,
              is_local: channelToDelete.is_local,
              is_duplicate: channelToDelete.is_duplicate
            }
          });
        }
      } catch (trackingError) {
        console.error('Failed to track channel deletions:', trackingError);
      }
    }
  },

  // Add new method for bulk updating channels with support for multiple categories
  bulkUpdateChannels: async (data: BulkUpdateChannelsInput): Promise<void> => {
    const { channelIds, versionId } = data;
    const updatePromises = [];

    // Handle category updates
    if (data.category_ids !== undefined) {
      console.log('[Mutations] Bulk updating categories:', {
        channelIds,
        categoryIds: data.category_ids,
        mode: data.categoryUpdateMode,
        categoryCount: data.category_ids?.length || 0
      });
      
      const categoryUpdatePromise = (async () => {
        const mode = data.categoryUpdateMode || 'replace';
        const categoryIds = data.category_ids || [];
        const defaultCategoryId = data.default_category_id || (categoryIds.length > 0 ? categoryIds[0] : null);
        
        // Special case: if replacing with empty array, it means remove all categories
        const isRemovingAllCategories = mode === 'replace' && categoryIds.length === 0;
        if (isRemovingAllCategories) {
          console.log('[Mutations] Special case: Removing all categories from channels:', channelIds);
        }

        // Process each channel
        for (const channelId of channelIds) {
          if (mode === 'replace') {
            console.log(`[Mutations] Replacing categories for channel ${channelId}:`, {
              oldCategories: 'will be deleted',
              newCategories: categoryIds,
              hasCategories: categoryIds.length > 0
            });
            
            try {
              // First delete all existing category relationships
              const { error: deleteError } = await supabase
                .from('channel_categories')
                .delete()
                .eq('channel_id', channelId);
                
              if (deleteError) {
                console.error(`[Mutations] Error deleting categories for channel ${channelId}:`, deleteError);
                throw deleteError;
              }
              
              // Then insert new ones, but only if there are categories to insert
              if (categoryIds.length > 0) {
                const { error: insertError } = await supabase
                  .from('channel_categories')
                  .insert(
                    categoryIds.map(categoryId => ({
                      channel_id: channelId,
                      category_id: categoryId,
                      is_default: !!(categoryId === defaultCategoryId)
                    }))
                  );
                  
                if (insertError) {
                  console.error(`[Mutations] Error inserting categories for channel ${channelId}:`, insertError);
                  throw insertError;
                }
              } else {
                // If no categories selected, just set category_id to NULL
                const { error: updateError } = await supabase
                  .from('channels')
                  .update({ category_id: null })
                  .eq('id', channelId);
                  
                if (updateError) {
                  console.error(`[Mutations] Error setting category_id to null for channel ${channelId}:`, updateError);
                  throw updateError;
                }
                
                console.log('[Mutations] Removed all categories from channel:', channelId);
              }
            } catch (error) {
              console.error(`[Mutations] Error in replace operation for channel ${channelId}:`, error);
              throw error;
            }
          }
          else if (mode === 'add') {
            // Skip if no categories to add
            if (categoryIds.length === 0) continue;
            
            // Get existing categories
            const { data: existingCategories } = await supabase
              .from('channel_categories')
              .select('category_id, is_default')
              .eq('channel_id', channelId);
            
            const existingCategoryIds = (existingCategories || []).map(c => c.category_id);
            const hasExistingDefault = (existingCategories || []).some(c => c.is_default);
            
            console.log(`[Mutations] Adding categories to channel ${channelId}:`, {
              existing: existingCategoryIds,
              toAdd: categoryIds,
              defaultCategory: defaultCategoryId,
              hasExistingDefault
            });
            
            // Add only new categories - categories not already associated with this channel
            const newCategories = categoryIds.filter(
              id => !existingCategoryIds.includes(id)
            ).map(categoryId => {
              // Special case: when adding new categories and one of them should be default
              // Only mark as default if:
              // 1. This is the selected default category, AND
              // 2. Either there's no existing default OR this is explicitly meant to be the new default
              const shouldBeDefault = 
                categoryId === defaultCategoryId && 
                (!hasExistingDefault || (defaultCategoryId && existingCategoryIds.includes(defaultCategoryId)));
                
              return {
              channel_id: channelId,
              category_id: categoryId,
                is_default: !!shouldBeDefault
              };
            });
            
            if (newCategories.length > 0) {
              // Insert the new categories
              const { error: insertError } = await supabase
                .from('channel_categories')
                .insert(newCategories);
              
              if (insertError) {
                console.error(`[Mutations] Error inserting categories for channel ${channelId}:`, insertError);
                throw insertError;
              }
            }
            
            // Handle default category updates - this might be a new or existing category
            if (defaultCategoryId) {
              // If the default is one of the existing categories (not a new one)
              if (existingCategoryIds.includes(defaultCategoryId)) {
                const { error: resetError } = await supabase
                  .from('channel_categories')
                  .update({ is_default: false })
                  .eq('channel_id', channelId);
                  
                if (resetError) {
                  console.error(`[Mutations] Error resetting default status for channel ${channelId}:`, resetError);
                  throw resetError;
                }
                
                const { error: updateError } = await supabase
                  .from('channel_categories')
                  .update({ is_default: true })
                  .eq('channel_id', channelId)
                  .eq('category_id', defaultCategoryId);
                  
                if (updateError) {
                  console.error(`[Mutations] Error setting default category for channel ${channelId}:`, updateError);
                  throw updateError;
                }
              }
              // If the default is one of the new categories, it's already set correctly in the newCategories array
              // We just need to make sure no other categories are marked as default
              else if (categoryIds.includes(defaultCategoryId) && hasExistingDefault) {
                const { error: updateError } = await supabase
                  .from('channel_categories')
                  .update({ is_default: false })
                  .eq('channel_id', channelId)
                  .not('category_id', 'eq', defaultCategoryId);
                  
                if (updateError) {
                  console.error(`[Mutations] Error updating existing categories default status for channel ${channelId}:`, updateError);
                  throw updateError;
                }
              }
            }
          }
          else if (mode === 'remove') {
            // Skip if no categories to remove
            if (categoryIds.length === 0) continue;
            
            // First, get the existing categories for this channel
            const { data: existingCategories, error: fetchError } = await supabase
              .from('channel_categories')
              .select('id, category_id, is_default')
              .eq('channel_id', channelId);
              
            if (fetchError) {
              console.error(`[Mutations] Error fetching categories for channel ${channelId}:`, fetchError);
              throw fetchError;
            }
            
            // Filter to only include categories that exist on this channel
            const existingCategoryIds = (existingCategories || []).map(c => c.category_id);
            const categoriesToRemove = categoryIds.filter(id => existingCategoryIds.includes(id));
            
            console.log(`[Mutations] Removing categories from channel ${channelId}:`, {
              requested: categoryIds,
              existing: existingCategoryIds,
              toRemove: categoriesToRemove
            });
            
            // Only proceed if there are actual categories to remove
            if (categoriesToRemove.length > 0) {
              // Remove specified categories that exist on this channel
              const { error: deleteError } = await supabase
                .from('channel_categories')
                .delete()
                .eq('channel_id', channelId)
                .in('category_id', categoriesToRemove);
                
              if (deleteError) {
                console.error(`[Mutations] Error removing categories from channel ${channelId}:`, deleteError);
                throw deleteError;
              }
              
              // Ensure at least one category is default (if any remain)
              const { data: remainingCategories, error: remainingError } = await supabase
                .from('channel_categories')
                .select('id, category_id, is_default')
                .eq('channel_id', channelId);
                
              if (remainingError) {
                console.error(`[Mutations] Error fetching remaining categories for channel ${channelId}:`, remainingError);
                throw remainingError;
              }
              
              if (remainingCategories && remainingCategories.length > 0) {
                const hasDefault = remainingCategories.some(c => c.is_default);
                
                if (!hasDefault) {
                  const { error: updateError } = await supabase
                    .from('channel_categories')
                    .update({ is_default: true })
                    .eq('id', remainingCategories[0].id);
                    
                  if (updateError) {
                    console.error(`[Mutations] Error setting default category for channel ${channelId}:`, updateError);
                    throw updateError;
                  }
                }
              }
            } else {
              console.log(`[Mutations] No matching categories to remove from channel ${channelId}`);
            }
          }
        }
      })();
      
      updatePromises.push(categoryUpdatePromise);
    }
    // Handle legacy single category update
    else if (data.category_id !== undefined) {
      updatePromises.push(
        channelMutations.bulkUpdateCategory(channelIds, data.category_id)
      );
    }
    
    // Handle plan updates - only update plans if explicitly included in the request
    // Check for data.plan_ids !== undefined instead of just data.plan_ids
    // This ensures we only update plans when the UI specifically includes plan_ids
    if (data.plan_ids !== undefined) {
      console.log('[Mutations] Bulk updating plans:', {
        channelIds,
        planIds: data.plan_ids,
        planCount: data.plan_ids?.length || 0
      });
      updatePromises.push(
        channelMutations.bulkUpdatePlans(channelIds, data.plan_ids, versionId)
      );
    }
    
    // Handle description updates
    if (data.description !== undefined) {
      updatePromises.push(
        supabase
          .from('channels')
          .update({ description: data.description })
          .in('id', channelIds)
      );
    }
    
    // Handle price updates
    if (data.price !== undefined) {
      updatePromises.push(
        supabase
          .from('channels')
          .update({ price: data.price })
          .in('id', channelIds)
      );
    }
    
    // Handle is_local updates
    if (data.is_local !== undefined) {
      console.log('[Mutations] Bulk updating is_local status:', {
        channelIds,
        is_local: data.is_local
      });
      updatePromises.push(
        supabase
          .from('channels')
          .update({ is_local: data.is_local })
          .in('id', channelIds)
      );
    }
    
    // Handle is_duplicate updates
    if (data.is_duplicate !== undefined) {
      console.log('[Mutations] Bulk updating is_duplicate status:', {
        channelIds,
        is_duplicate: data.is_duplicate
      });
      updatePromises.push(
        supabase
          .from('channels')
          .update({ is_duplicate: data.is_duplicate })
          .in('id', channelIds)
      );
    }
    
    // Execute all updates in parallel
    await Promise.all(updatePromises);
  }
};
